import { useState } from "react";

import { useNavigate } from "react-router";

import { useQuery } from "@tanstack/react-query";

import { FolderKanbanIcon, LockIcon, SearchIcon, TicketIcon } from "lucide-react";

import { Command, CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";

import { EmptyState } from "@/shared/components/EmptyState";
import Error from "@/shared/components/Error";
import { SearchResultSkeleton } from "@/shared/components/SearchResultSkeleton";
import { useDebouncedValue } from "@/shared/hooks/useDebouncedValue";
import { PERMISSIONS } from "@/shared/lib/constants/permissions";
import { QUERY_EXPIRATIONS, QUERY_KEYS } from "@/shared/lib/constants/queries";
import { searchMine } from "@/shared/lib/services/user";
import { SearchProject, SearchResults, SearchTicket } from "@/shared/lib/types/user";
import { AppError } from "@/shared/lib/utils/errors";

const SEARCH_DEBOUNCE_MS = 250;

interface GlobalSearchFormProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  permissions: string[];
}

export default function GlobalSearchForm(props: GlobalSearchFormProps) {
  const navigate = useNavigate();
  const [term, setTerm] = useState("");
  const debouncedTerm = useDebouncedValue(term, SEARCH_DEBOUNCE_MS);

  const canSearch = props.permissions.includes(PERMISSIONS.USER_PROJECTS_READ);
  const searchTerm = debouncedTerm.trim();

  const { data, isFetching, error, refetch } = useQuery<SearchResults, AppError | Error>({
    queryKey: [QUERY_KEYS.SEARCH_MINE, searchTerm],
    queryFn: () => searchMine(searchTerm),
    enabled: props.open && canSearch && searchTerm.length > 0,
    staleTime: QUERY_EXPIRATIONS.SEARCH_MINE,
  });

  const projects = data?.projects ?? [];
  const tickets = data?.tickets ?? [];
  const hasResults = projects.length > 0 || tickets.length > 0;

  // Covers the window between a keystroke and the debounced term catching up, so the
  // palette doesn't show results for a term the user has already moved on from.
  const isSearching = isFetching || term.trim() !== searchTerm;

  function handleOpenChange(open: boolean) {
    if (!open) setTerm("");
    props.onOpenChange(open);
  }

  function handleSelectProject(project: SearchProject) {
    handleOpenChange(false);
    navigate(`/projects/${project.id}`);
  }

  function handleSelectTicket(ticket: SearchTicket) {
    handleOpenChange(false);
    navigate(`/projects/${ticket.projectId}/tickets/${ticket.id}`);
  }

  return (
    <CommandDialog open={props.open} onOpenChange={handleOpenChange} title="Search" description="Search for projects and tickets">
      {/* Results come back already filtered and ranked by the API, so cmdk must not filter them again. */}
      <Command shouldFilter={false}>
        <CommandInput placeholder="Search projects and tickets…" value={term} onValueChange={setTerm} />
        <CommandList>
          {!canSearch ? (
            <EmptyState icon={LockIcon} title="No access" description="You don't have permission to search projects and tickets." className="py-10" />
          ) : error ? (
            <div className="p-3">
              <Error variant="alert" title="Search failed" error={error} onRetry={() => refetch()} />
            </div>
          ) : searchTerm.length === 0 ? (
            <EmptyState icon={SearchIcon} title="Search projects and tickets" description="Start typing to find a project by name, or a ticket by key or title." className="py-10" />
          ) : isSearching ? (
            <div className="flex flex-col p-1.5">
              {Array.from({ length: 4 }).map((_, i) => (
                <SearchResultSkeleton key={i} />
              ))}
            </div>
          ) : !hasResults ? (
            <CommandEmpty>No results found.</CommandEmpty>
          ) : (
            <>
              {projects.length > 0 && (
                <CommandGroup heading="Projects">
                  {projects.map((project) => (
                    <CommandItem key={project.id} value={project.id} onSelect={() => handleSelectProject(project)}>
                      <FolderKanbanIcon className="text-primary" />
                      {project.name}
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
              {projects.length > 0 && tickets.length > 0 && <CommandSeparator />}
              {tickets.length > 0 && (
                <CommandGroup heading="Tickets">
                  {tickets.map((ticket) => (
                    <CommandItem key={ticket.id} value={ticket.id} onSelect={() => handleSelectTicket(ticket)}>
                      <TicketIcon className="text-muted-foreground" />
                      <span className="font-mono text-xs text-muted-foreground">{ticket.key}</span>
                      <span className="truncate">{ticket.title}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </>
          )}
        </CommandList>
      </Command>
    </CommandDialog>
  );
}
